/* eslint-disable no-unsafe-optional-chaining */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    CommonService,
    Config,
    Destroy,
    FIELD_TYPE,
    MODALCONFIG,
    MODAL_SIZE,
    SelectFormFields,
    ToastService,
} from '@increff/commons';
import { finalize, Observable, of, takeUntil } from 'rxjs';
import {
    APP_RESOURCE,
    APP_ROLE,
    DOMAIN_ROLE,
} from '../../interfaces/application.interface';
import { DOMAIN_RESOURCE } from '../../interfaces/resource.interface';
import { USER } from '../../interfaces/user.interface';
import { ManagementService } from '../../services/management/management.service';
import { UsersService } from '../../services/users/users.service';
import { AuthNGuardService } from '../../services/authn-guard.service';

@Component({
    selector: 'com-role-management',
    templateUrl: './role-management.component.html',
    styleUrls: ['./role-management.component.scss'],
    viewProviders: [Destroy],
})
export class RoleManagementComponent {
    @Input() selectedUser: USER;
    @Input() flow: 'admin' | 'domain' = 'admin';
    @Input() appName: string;
    @Input() domainId: number;

    @Output() updatedUserDetailsEmitter = new EventEmitter<boolean>();

    roleManagementModalConfig: MODALCONFIG;

    userRoles: DOMAIN_ROLE[] = [];

    roleLevel: 'top' | 'resource' = 'top';

    selectedApplication = '';
    selectedAppResourceId = -1;
    selectedDomainResourceId = -1;
    allRoles: { [key: string]: APP_ROLE } = {};
    rolesToBeShown: APP_ROLE[] = [];
    topLevelRoleStateRetention: Array<number> = [];
    resourceLevelRoleStateRetention: Array<number> = [];
    applicationResources: APP_RESOURCE[] = [];
    domainResources: DOMAIN_RESOURCE[] = [];
    // domainResourcesToBeShown: DOMAIN_RESOURCE[] = [];

    applicationSelect: SelectFormFields = {
        key: 'applicationSelectRoleManagement',
        label: 'Application',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: true,
        options: []
    };

    resourceTypeSelect: SelectFormFields = {
        key: 'resourceTypeSelect',
        label: 'Resource Type',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: true,
        options: []
    };

    resourceValueSelect: SelectFormFields = {
        key: 'resourceValueSelect',
        label: 'Resource Value',
        type: FIELD_TYPE.CUSTOM_SELECT,
        required: true,
        options: []
    };

    constructor(
        private managementService: ManagementService,
        private authService: AuthNGuardService,
        private userService: UsersService,
        private readonly destroy$: Destroy,
        private commonService: CommonService,
        private toast: ToastService,
        private config: Config
    ) {
        this.setRoleManagementModalData();
        this.appName = this.config?.startupData?.defaultAppName;
        this.domainId = this.config?.startupData?.defaultDomainId;
    }

    get appNamesMap(): Record<string, {[key: string]: APP_ROLE | any}> {
        return this.managementService?.appNamesMap;
    }

    roleStateRetentionMethod(spy: any, role: DOMAIN_ROLE): void {
        if (spy?.checked)
            this.roleLevel === 'top'
                ? this.topLevelRoleStateRetention?.push(role?.id)
                : this.resourceLevelRoleStateRetention?.push(role?.id);
        else
            this.roleLevel === 'top'
                ? (this.topLevelRoleStateRetention =
                      this.topLevelRoleStateRetention?.filter(
                          (obj) => obj != role?.id
                      ))
                : (this.resourceLevelRoleStateRetention =
                      this.resourceLevelRoleStateRetention?.filter(
                          (obj) => obj != role?.id
                      ));

        this.topLevelRoleStateRetention = [
            ...new Set(this.topLevelRoleStateRetention),
        ];
        this.resourceLevelRoleStateRetention = [
            ...new Set(this.resourceLevelRoleStateRetention),
        ];
    }

    ngOnChanges({ selectedUser, appName }): void {
        if (selectedUser && !selectedUser?.firstChange) {
            this.roleManagementModalConfig.pillData =
                this.selectedUser?.fullName || '';
            this.getUserRoles();
        }

        if (
            appName &&
            appName?.currentValue &&
            !this.appNamesMap?.[this.flow ==='admin' ? this.appName : 'currentDomain']
        )
            this.getAllRoles$().subscribe({
                next: (response) => {
                    this.createAppMap(response);
                },
            });
    }

    ngOnInit(): void {
        if (this.domainId == -1)
            this.getAllRoles$().subscribe({
                next: (response) => {
                    this.createAppMap(response);
                },
            });
    }

    ngAfterViewInit() {
        this.initTooltip();
    }

    initTooltip(): void {
        this.commonService?.initTooltip('com-role-management');
    }

    objectKeys(obj: object): any {
        return Object?.keys(obj ?? {});
    }

    isRoleChecked(roleId: number): boolean {
        return this.roleLevel === 'top'
            ? this.topLevelRoleStateRetention?.filter(
                (role) => role == roleId
            )?.[0]
                ? true
                : false
            : this.resourceLevelRoleStateRetention?.filter(
                (role) => role == roleId
            )?.[0]
                ? true
                : false;
    }

    resetViewData(): void {
        this.rolesToBeShown = [];
        this.applicationResources = [];
        this.domainResources = [];
        this.applicationResources = [];
        this.domainResources = [];
        this.selectedApplication = this.applicationSelect.value = '';
        this.selectedAppResourceId = this.resourceTypeSelect.value = this.selectedDomainResourceId = this.resourceValueSelect.value = -1;
        this.applicationSelect = {...this.applicationSelect};
        this.resourceTypeSelect = {...this.resourceTypeSelect};
        this.resourceValueSelect = {...this.resourceValueSelect};
    }

    getAllRoles$(
        appResourceId?: number,
        domainResourceId?: number
    ): Observable<APP_ROLE[]> {
        if (!this.appName && this.domainId != -1) return of([]);
        return this.managementService
            ?.getAllRoles(
                this.appName,
                appResourceId,
                domainResourceId,
                this.flow
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            );
    }

    getAllRoles(appResourceId?: number, domainResourceId?: number): void {
        this.managementService
            ?.getAllRoles(
                this.appName,
                appResourceId,
                domainResourceId,
                this.flow
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            )
            .subscribe({
                next: (response) => {
                    this.rolesToBeShown = response;
                },
            });
    }

    createAppMap(roles: APP_ROLE[]): void {
        for (const obj of roles) {
            if(obj?.resourceId) continue;
            const key = obj['id'];
            const appname = obj['roleName']?.split('.')[0];
            const keyStr = appname == obj?.['roleName'] ? 'legacy' : appname;
            const parentObjKey =
                this.flow === 'admin' ? this.appName : 'currentDomain';
            this.appNamesMap[parentObjKey] = this.appNamesMap?.[parentObjKey]
                ? this.appNamesMap?.[parentObjKey]
                : {};
            this.appNamesMap[parentObjKey]['All'] = [
                ...(this.appNamesMap[parentObjKey]['All'] ?? []),
                ...[obj],
            ];
            this.appNamesMap[parentObjKey][keyStr] = this.appNamesMap?.[
                parentObjKey
            ]?.[keyStr]
                ? [...this.appNamesMap?.[parentObjKey]?.[keyStr], ...[obj]]
                : [obj];
            this.allRoles[key] = obj;
        }
        this.setApplicationSelectOptions();
    }

    setApplicationSelectOptions(): void {
        this.applicationSelect.options = Object.keys((this.appNamesMap?.[this.flow === 'admin' ? this.appName : 'currentDomain'] ?? {}) as Record<string,any>)?.map((appName)=> ({displayKey: appName, valueKey: appName}));
        this.applicationSelect = {...this.applicationSelect};
    }

    setResourceTypeSelectOptions(): void {
        this.resourceTypeSelect.options = this.applicationResources?.map((resourceObj) => ({displayKey: `${resourceObj?.resource}` , valueKey: resourceObj?.id}));
        this.resourceTypeSelect = {...this.resourceTypeSelect};
    }

    setResourceValueSelectOptions(): void {
        this.resourceValueSelect.options = this.domainResources?.map((resourceObj) => ({displayKey: `${resourceObj?.value} (${resourceObj?.description?.substring(0,20)})` , valueKey: resourceObj?.id }));
        this.resourceValueSelect = {...this.resourceValueSelect};
    }

    getApplicationResources(): void {
        this.managementService
            ?.getApplicationResources(this.flow === 'admin' ? this.appName : '')
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            )
            .subscribe({
                next: (response) => {
                    this.applicationResources = response;
                    this.setResourceTypeSelectOptions();
                },
            });
    }

    getDomainResources(appResourceId: number): void {
        this.managementService
            ?.getAllDomainResources()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            )
            .subscribe({
                next: (response) => {
                    this.domainResources = response;
                    this.domainResources = this.domainResources?.filter(
                        (domainResource) =>
                            domainResource?.resourceId == appResourceId
                    );
                    this.setResourceValueSelectOptions();
                },
            });
    }

    getUserRoles(domainResourceId?: number): void {
        this.managementService
            ?.getUserRoles(
                this.selectedUser?.id,
                this.flow === 'admin' ? this.domainId : undefined,
                domainResourceId,
                this.flow
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            )
            .subscribe({
                next: (response) => {
                    this.topLevelRoleStateRetention = [];
                    this.resourceLevelRoleStateRetention = [];
                    this.userRoles = response;
                    this.userRoles?.forEach((role) => {
                        this.roleLevel == 'top'
                            ? this.topLevelRoleStateRetention?.push(role?.role)
                            : this.resourceLevelRoleStateRetention?.push(
                                role?.role
                            );
                    });
                    this.topLevelRoleStateRetention = [
                        ...new Set(this.topLevelRoleStateRetention),
                    ];
                    this.resourceLevelRoleStateRetention = [
                        ...new Set(this.resourceLevelRoleStateRetention),
                    ];
                },
            });
    }

    updateUserRoles(arg1: any, cancelBtn: HTMLButtonElement): void {
        this.managementService
            ?.updateUserRoles(
                this.selectedUser?.id,
                this.roleLevel === 'top'
                    ? this.topLevelRoleStateRetention
                    : this.resourceLevelRoleStateRetention,
                this.flow === 'admin' ? this.domainId : undefined,
                this.roleLevel === 'resource'
                    ? this.selectedDomainResourceId
                    : undefined,
                this.flow
            )
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.initTooltip())
            )
            .subscribe({
                next: () => {
                    this.toast?.success(
                        `Roles updated successfully for ${this.selectedUser?.fullName}.`
                    );
                    this.roleLevel === 'top' ? cancelBtn?.click() : '';
                    this.updatedUserDetailsEmitter?.emit(true);
                    if (
                        this.selectedUser.id ===
                        this.authService?.currentUser.id
                    ) void this.authService?.setCurrentUserObs(undefined, true);
                },
            });
    }

    roleManagementPopupResetView(): void {
        this.resetViewData();
        this.roleLevel = 'top';
    }

    setRoleManagementModalData(): void {
        this.roleManagementModalConfig = new MODALCONFIG({
            selector: 'role-management-modal',
            heading: 'Role Management',
            theme: 'primary',
            displayDefaultFooterContent: true,
            scrollable: false,
            size: MODAL_SIZE.MD,
            enableBackdrop: true,
            enableKeyboard: false,
            submitAction: this.updateUserRoles.bind(this),
            cancelAction: this.roleManagementPopupResetView.bind(this),
        });
    }
}
